import { useEffect, useMemo, useState } from 'react';
import { KeyRound, Lock, LockOpen, Nfc, ScanLine, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Skeleton } from '@/components/ui/Skeleton';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { AchievementGrid, LevelBadge, StatsStrip } from '@/components/work/WorkAchievements';
import { WorkerChip, QuietEmpty } from './shared';
import {
  useCredentialStatus,
  useLeaderboard,
  useOpenSessions,
  useSetBadge,
  useSetPin,
  useUnlockCredentials,
  useWorkProfileStats,
} from '@/features/workMode/hooks';
import { describeWorkError, formatDuration } from '@/features/workMode/api';
import { scrapRate } from '@/features/workMode/gamification';
import { useBadgeScanner } from '@/features/workMode/kiosk';
import type { WorkLookups, WorkPerson } from '@/features/workMode/useLookups';
import type { WorkCredentialStatus } from '@/features/workMode/types';

interface PeopleTabProps {
  lookups: WorkLookups;
  canManage: boolean;
}

/**
 * Who can sign in at a bench and how: PIN / badge management, lockouts,
 * and a per-person drawer with their level, stats and badges. Also the
 * weekly board — ranked on output, but scrap and safety sit right beside it
 * so a fast, sloppy week does not read as a win.
 */
export function PeopleTab({ lookups, canManage }: PeopleTabProps) {
  const { data: credentials, isLoading: credLoading } = useCredentialStatus();
  const { data: sessions = [] } = useOpenSessions();
  const { data: board = [], isLoading: boardLoading } = useLeaderboard(7);
  const [pinFor, setPinFor] = useState<WorkPerson | null>(null);
  const [badgeFor, setBadgeFor] = useState<WorkPerson | null>(null);
  const [viewing, setViewing] = useState<WorkPerson | null>(null);
  const unlock = useUnlockCredentials();

  const workingAt = useMemo(() => new Map(sessions.map((s) => [s.user_id, s.workstation_id])), [sessions]);
  const people = lookups.peopleList.filter((p) => p.isActive);
  const now = Date.now();

  if (lookups.loading || credLoading) {
    return <Skeleton className="h-72 rounded-2xl" />;
  }

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-border bg-card">
        <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Trophy className="h-4 w-4 text-info" />
              This week on the floor
            </h3>
          </div>
        </header>
        {boardLoading ? (
          <Skeleton className="m-4 h-32" />
        ) : board.length === 0 ? (
          <div className="p-4">
            <QuietEmpty>No units logged in the last 7 days.</QuietEmpty>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Person</TableHead>
                  <TableHead className="text-right">Units</TableHead>
                  <TableHead className="text-right">Scrap</TableHead>
                  <TableHead className="text-right">Avg cycle</TableHead>
                  <TableHead className="text-right">Caught</TableHead>
                  <TableHead className="text-right">Ideas</TableHead>
                  <TableHead className="text-right">Safety</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {board.map((row, i) => {
                  const rate = scrapRate(row.units, row.scrapped);
                  return (
                    <TableRow key={row.user_id} className="cursor-pointer" onClick={() => setViewing(lookups.person(row.user_id))}>
                      <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                      <TableCell>
                        <WorkerChip person={lookups.person(row.user_id)} size={28} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{row.units}</TableCell>
                      <TableCell className={cn('text-right tabular-nums', rate !== null && rate > 0.05 ? 'text-fresh-salmon-dark' : rate === 0 ? 'text-bright-moss-dark' : '')}>
                        {rate === null ? '—' : `${(rate * 100).toFixed(1)}%`}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatDuration(row.avg_cycle_seconds)}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.defects_reported}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.suggestions}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.safety_notes}</TableCell>
                      <TableCell className="text-right tabular-nums">{(row.seconds_worked / 3600).toFixed(1)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-border bg-card">
        <header className="border-b border-border px-4 py-3">
          <h3 className="text-sm font-medium text-foreground">Sign-in credentials</h3>
        </header>
        {people.length === 0 ? (
          <div className="p-4">
            <QuietEmpty>No active people in this organisation.</QuietEmpty>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead>PIN</TableHead>
                  <TableHead>Badge</TableHead>
                  <TableHead>Right now</TableHead>
                  {canManage && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {people.map((p) => {
                  const cred: WorkCredentialStatus | undefined = credentials?.get(p.id);
                  const locked = !!cred?.locked_until && Date.parse(cred.locked_until) > now;
                  const station = workingAt.get(p.id);
                  return (
                    <TableRow key={p.id}>
                      <TableCell>
                        <button type="button" className="text-left" onClick={() => setViewing(p)}>
                          <WorkerChip person={p} sub={p.jobTitle ?? roleLabel(p.role)} />
                        </button>
                      </TableCell>
                      <TableCell>
                        {locked ? (
                          <Badge variant="warning-soft">
                            <Lock className="mr-1 h-3 w-3" />
                            Locked
                          </Badge>
                        ) : cred?.has_pin ? (
                          <Badge variant="success-soft">Set</Badge>
                        ) : (
                          <Badge variant="muted">None</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {cred?.has_badge ? (
                          <Badge variant="info-soft">
                            <Nfc className="mr-1 h-3 w-3" />…{cred.badge_last4}
                          </Badge>
                        ) : (
                          <Badge variant="muted">None</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {station ? <span className="text-foreground">At {lookups.stationName(station)}</span> : '—'}
                      </TableCell>
                      {canManage && (
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            {locked && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={unlock.isPending}
                                onClick={() =>
                                  unlock.mutate(
                                    { userId: p.id },
                                    {
                                      onSuccess: () => toast.success(`${p.name} unlocked.`),
                                      onError: (e) => toast.error(describeWorkError(e)),
                                    },
                                  )
                                }
                              >
                                <LockOpen className="h-3.5 w-3.5" />
                                Unlock
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => setPinFor(p)}>
                              <KeyRound className="h-3.5 w-3.5" />
                              {cred?.has_pin ? 'Reset PIN' : 'Set PIN'}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setBadgeFor(p)}>
                              <ScanLine className="h-3.5 w-3.5" />
                              Badge
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <SetPinDialog person={pinFor} onOpenChange={(o) => !o && setPinFor(null)} />
      <BadgeDialog person={badgeFor} hasBadge={!!(badgeFor && credentials?.get(badgeFor.id)?.has_badge)} onOpenChange={(o) => !o && setBadgeFor(null)} />
      <PersonSheet person={viewing} lookups={lookups} workingAt={viewing ? (workingAt.get(viewing.id) ?? null) : null} onOpenChange={(o) => !o && setViewing(null)} />
    </div>
  );
}

function roleLabel(role: string): string {
  return role.replace('_', ' ');
}

/** Managers set or reset a PIN for someone. Also reused by Account Settings for self. */
export function SetPinDialog({
  person,
  onOpenChange,
  self = false,
}: {
  person: WorkPerson | null;
  onOpenChange: (open: boolean) => void;
  self?: boolean;
}) {
  const setPin = useSetPin();
  const [pin, setPinValue] = useState('');
  const [confirm, setConfirm] = useState('');

  const personId = person?.id ?? null;
  useEffect(() => {
    setPinValue('');
    setConfirm('');
  }, [personId]);

  if (!person) return null;
  const valid = /^\d{4,8}$/.test(pin);
  const matches = pin === confirm;
  const weak = valid && (/^(\d)\1+$/.test(pin) || '0123456789'.includes(pin) || '9876543210'.includes(pin));

  const save = () => {
    if (!valid || !matches) return;
    setPin.mutate(
      { userId: person.id, pin },
      {
        onSuccess: () => {
          toast.success(self ? 'Your PIN is set.' : `PIN set for ${person.name}.`);
          onOpenChange(false);
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <Dialog open={!!person} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{self ? 'Your Work Mode PIN' : `PIN for ${person.name}`}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="pin-new">New PIN</Label>
            <Input id="pin-new" type="password" inputMode="numeric" autoComplete="off" value={pin} onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 8))} autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pin-confirm">Confirm</Label>
            <Input id="pin-confirm" type="password" inputMode="numeric" autoComplete="off" value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\D/g, '').slice(0, 8))} />
          </div>
          <p className={cn('min-h-4 text-xs', weak ? 'text-fresh-salmon-dark' : 'text-muted-foreground')}>
            {pin && !valid ? 'Use 4 to 8 digits.' : confirm && !matches ? 'The two entries do not match.' : weak ? 'That is easy to guess — consider something less obvious.' : ''}
          </p>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || !matches || setPin.isPending}>
              Save PIN
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Assign a keyfob / card by scanning it (keyboard-wedge reader) or typing its number. */
export function BadgeDialog({ person, hasBadge, onOpenChange }: { person: WorkPerson | null; hasBadge: boolean; onOpenChange: (open: boolean) => void }) {
  const setBadge = useSetBadge();
  const [code, setCode] = useState('');
  const [scanned, setScanned] = useState(false);
  const open = !!person;

  useEffect(() => {
    if (open) {
      setCode('');
      setScanned(false);
    }
  }, [open]);

  useBadgeScanner(open, (c) => {
    setCode(c);
    setScanned(true);
  });

  if (!person) return null;
  const valid = code.trim().length >= 4;

  const save = (value: string | null) => {
    setBadge.mutate(
      { userId: person.id, badge: value },
      {
        onSuccess: () => {
          toast.success(value ? `Badge assigned to ${person.name}.` : `Badge removed from ${person.name}.`);
          onOpenChange(false);
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Badge for {person.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div
            className={cn(
              'flex items-center gap-3 rounded-xl border p-4',
              scanned ? 'border-bright-moss bg-bright-moss/15' : 'border-dashed border-border bg-secondary/40',
            )}
          >
            <Nfc className={cn('h-8 w-8 shrink-0', scanned ? 'text-bright-moss-dark' : 'text-muted-foreground')} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{scanned ? 'Badge read' : 'Waiting for a scan…'}</p>
              <p className="truncate font-mono text-xs text-muted-foreground">{scanned ? code : 'Hold the fob to the reader'}</p>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="badge-code">Badge number</Label>
            <Input
              id="badge-code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setScanned(false);
              }}
              placeholder="e.g. 0A3F9C21"
              autoComplete="off"
              className="font-mono"
            />
          </div>
        </div>
        <DialogFooter className="sm:justify-between">
          <div>
            {hasBadge && (
              <Button type="button" variant="ghost" className="text-fresh-salmon-dark" disabled={setBadge.isPending} onClick={() => save(null)}>
                Remove badge
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={!valid || setBadge.isPending} onClick={() => save(code.trim())}>
              Assign
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PersonSheet({
  person,
  lookups,
  workingAt,
  onOpenChange,
}: {
  person: WorkPerson | null;
  lookups: WorkLookups;
  workingAt: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: stats, isLoading } = useWorkProfileStats(person?.id ?? null);
  return (
    <Sheet open={!!person} onOpenChange={onOpenChange}>
      <SheetContent>
        {person && (
          <>
            <SheetHeader>
              <WorkerChip person={person} sub={person.jobTitle ?? roleLabel(person.role)} size={48} />
              <SheetTitle className="sr-only">{person.name}</SheetTitle>
              <SheetDescription>{workingAt ? `Working at ${lookups.stationName(workingAt)} right now.` : 'Not at a station right now.'}</SheetDescription>
            </SheetHeader>
            <SheetBody className="space-y-6">
              {isLoading || !stats ? (
                <Skeleton className="h-40" />
              ) : (
                <>
                  <LevelBadge stats={stats} size="lg" />
                  <StatsStrip stats={stats} />
                  <div>
                    <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Badges</p>
                    <AchievementGrid stats={stats} />
                  </div>
                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Hours on the bench</dt>
                      <dd className="text-foreground">{(stats.seconds_worked_total / 3600).toFixed(1)} h</dd>
                    </div>
                    <div>
                      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Stations worked</dt>
                      <dd className="text-foreground">{stats.stations_worked}</dd>
                    </div>
                    <div>
                      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Best day</dt>
                      <dd className="text-foreground">{stats.best_day_units} units</dd>
                    </div>
                    <div>
                      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Avg mood (30d)</dt>
                      <dd className="text-foreground">{stats.avg_mood_30d == null ? '—' : `${stats.avg_mood_30d.toFixed(1)} / 5`}</dd>
                    </div>
                  </dl>
                </>
              )}
            </SheetBody>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
